import type {
  UpdateZohoConfigRequest,
  ZohoAuthorizeUrlResponse,
  ZohoConfigDto,
  ZohoTestResult,
  ZohoTestTicketResponse,
  ZohoTicketFieldsResponse,
} from '@field-support/shared';
import { api } from '@/shared/api';

/** The Zoho Desk ticket layout's fields (cached server-side); fails while Zoho is not connected. */
export function fetchZohoTicketFields(signal?: AbortSignal): Promise<ZohoTicketFieldsResponse> {
  return api<ZohoTicketFieldsResponse>('/engine/zoho/ticket-fields', { signal });
}

/** The configuration without secrets: client id/secret and the refresh token are only `has*` booleans. */
export function fetchZohoConfig(signal?: AbortSignal): Promise<ZohoConfigDto> {
  return api<ZohoConfigDto>('/engine/zoho/config', { signal });
}

/** `clientId` / `clientSecret` are sent only when typed: leaving them out keeps the stored (encrypted) value. */
export function updateZohoConfig(body: UpdateZohoConfigRequest): Promise<ZohoConfigDto> {
  return api<ZohoConfigDto>('/engine/zoho/config', { method: 'PUT', body });
}

/** A one-time Zoho consent URL; Zoho redirects back to /integrations/zoho?oauth=success|error&reason=CODE. */
export function fetchZohoAuthorizeUrl(): Promise<ZohoAuthorizeUrlResponse> {
  return api<ZohoAuthorizeUrlResponse>('/engine/zoho/authorize-url');
}

/** Forgets the refresh token; visits keep being stored, not synced. */
export function disconnectZoho(): Promise<ZohoConfigDto> {
  return api<ZohoConfigDto>('/engine/zoho/connection', { method: 'DELETE' });
}

/** Refreshes a token and reads the department; the outcome is recorded on the configuration. */
export function testZohoConnection(): Promise<ZohoTestResult> {
  return api<ZohoTestResult>('/engine/zoho/test-connection', { method: 'POST' });
}

/** Creates a ticket marked TEST from the active form (or draft) and the current mappings; returns what was sent. */
export function createZohoTestTicket(): Promise<ZohoTestTicketResponse> {
  return api<ZohoTestTicketResponse>('/engine/zoho/test-ticket', { method: 'POST' });
}
